import React, { createRef } from 'react';
import { ScaleBand, ScaleLinear, scaleBand, scaleLinear, max } from 'd3';
import {
  BottomOrdinalAxis,
  ScaleYGraphLines,
  ScaleYAxis,
  TickValues,
  LabelledRect,
} from '../Charts';
import css from './index.module.scss';
import { numberWithCommas, classNames, percentage } from '@fullstory/util/fmt';
import { FunnelDetails } from '@fullstory/redux';
import { FormattedMessage } from 'react-intl';

interface ConversionGraphicProps {
  data: FunnelDetails[];
  topXPadding: number;
  bottomXPadding: number;
  leftYPadding: number;
  rightYPadding: number;
  height: number;
  /** Float between 0 and 1, representing the padding between individual bars */
  barPadding: number;
}

interface ConversionGraphicState {
  width: number;
}

export class ConversionGraphic extends React.Component<
  ConversionGraphicProps,
  ConversionGraphicState
> {
  private _svg = createRef<SVGSVGElement>();
  private _overlay = createRef<HTMLDivElement>();
  private timeoutId: number | undefined;

  state = { width: 0 };

  static defaultProps = {
    height: 200,
    topXPadding: 24,
    bottomXPadding: 24,
    leftYPadding: 60,
    rightYPadding: 70,
    barPadding: 0.7,
  };

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
    }
  }

  onResize = () => {
    // conditional rendering is leading to componentDidMount without being attached to the DOM
    // in that case, the clientWidth is 0, so try again next frame.
    if (this._svg.current?.clientWidth === 0) {
      this.timeoutId = window.setTimeout(() => {
        this.timeoutId = 0;
        this.onResize();
      }, 1);
    }
    this.setState({
      width: this._svg.current?.clientWidth ?? 0,
    });
  };

  sizes() {
    const { height, topXPadding, bottomXPadding, leftYPadding, rightYPadding } = this.props;
    const yPadding = leftYPadding + rightYPadding;
    const xPadding = topXPadding + bottomXPadding;
    const chartWidth = Math.max(this.state.width, yPadding);
    return {
      chartHeight: height,
      chartWidth,
      innerWidth: chartWidth - yPadding,
      innerHeight: height - xPadding,
      xPadding,
      yPadding,
    };
  }

  xScaleDomainMapFn(details: FunnelDetails, index: number): string {
    return details.label ? `${details.label}_${index + 1}` : `Step ${index + 1}`;
  }

  scales(): { xScale: ScaleBand<string>; yScale: ScaleLinear<number, number> } {
    const xScale = scaleBand();
    const yScale = scaleLinear();

    const sizes = this.sizes();

    yScale.domain([0, this.max]).range([sizes.innerHeight, this.props.topXPadding]).ticks(10);

    xScale
      .domain(this.props.data.map(this.xScaleDomainMapFn))
      .range([this.props.leftYPadding, this.props.leftYPadding + sizes.innerWidth])
      .paddingInner(this.props.barPadding);

    return {
      xScale,
      yScale,
    };
  }

  get max(): number {
    // ensure it's at least one, preventing NaN's
    return Math.max(max(this.props.data, d => d.count) || 0, 1);
  }

  scaleYTextRenderer = (d: TickValues) => percentage(d.value / this.max, true);

  tickValues(yScale: ScaleLinear<number, number>): TickValues[] {
    const tickValues: TickValues[] = [];
    const domain = yScale.domain();
    for (let i = 0; i < 6; i++) {
      const increment = 0.2;
      const value = (1 - increment * i) * domain[1]!;
      tickValues.push({
        value,
        position: yScale(value),
      });
    }
    return tickValues;
  }

  render() {
    const { xScale, yScale } = this.scales();
    const sizes = this.sizes();
    const tickValues = this.tickValues(yScale);
    return (
      <div
        data-component="ConversionGraphic"
        data-testid="conversionGraphicContainer"
        className={css.conversionGraphicContainer}
        style={{ height: this.props.height }}
      >
        <svg
          className={css.graphic}
          ref={this._svg}
          viewBox={`0 0 ${sizes.chartWidth} ${sizes.chartHeight}`}
        >
          <title hidden>
            <FormattedMessage defaultMessage="Conversion Funnel Graphic" id="dZKLxO" />
          </title>
          <ScaleYGraphLines
            scale={yScale}
            width={sizes.chartWidth}
            marginLeft={this.props.leftYPadding}
            marginRight={0}
            tickValues={tickValues}
          />
          <ScaleYAxis
            scale={yScale}
            textRenderer={this.scaleYTextRenderer}
            tickValues={tickValues}
          />
          <g data-testid="rect-container">
            {this.props.data.map((datum, i) => (
              <LabelledRect
                key={i}
                className={classNames(css.bar, 'fs-exclude')}
                label={numberWithCommas(datum.count)}
                x={xScale(this.xScaleDomainMapFn(datum, i)) ?? 0}
                y={yScale(datum.count)}
                height={sizes.innerHeight - yScale(datum.count)}
                width={xScale.bandwidth()}
                textPaddingY={-8}
                textPaddingX={xScale.bandwidth() / 2}
                labelClassName={css.label}
              />
            ))}
          </g>
          <BottomOrdinalAxis
            xScale={xScale}
            labels={this.props.data.map(d => d.label)}
            height={sizes.innerHeight}
            portalDiv={this._overlay.current ?? undefined}
            // step padding should be the min of leftYPadding and rightYPadding for the graph so that
            // labels do not bleed out of the graph footprint
            stepPadding={Math.min(this.props.leftYPadding, this.props.rightYPadding)}
            labelClassName={classNames(css.label)}
          />
        </svg>
        <div
          data-testid="overlay"
          className={css.overlay}
          ref={this._overlay}
          style={{ height: this.props.height }}
        />
      </div>
    );
  }
}
